import { JWMHtml } from "@components/JWMHtml/JWMHtml";
import { Icon } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { LinkButton } from "@jmc/solid-design-system/src/components/atoms/LinkButton/LinkButton";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { Footnote } from "@jwm/types/CMSFootnotes";
import { mdiKeyboardReturn } from "@mdi/js";
import { ApplicationState } from "@redux/modules/index";
import { Panes, set as setPanes } from "@redux/modules/panes";
import { scrollIntoView } from "@utils/scrollIntoView";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import style from "./style.module.scss";

type PropTypes = {
    placement?: string;
};

/**
 * FootnotesSection is a component that renders a list of footnotes that link back to the corresponding content on the page.
 */
export const FootnotesSection = (props: PropTypes): JSX.Element => {
    const { placement } = props;
    const { t } = useTranslation();
    const placementReferences = `${placement}|section:${t("References", { ns: "common" })}`;
    const footnotes = useSelector((state: ApplicationState) => state.footnotes);
    const dispatch = useDispatch();

    const hasFootnotes = (): boolean => {
        return footnotes.reduce((acc, current) => {
            if (current.footnotes.find((f) => f.index > 0)) {
                acc = true;
            }
            return acc;
        }, false as boolean);
    };

    const onScrollHandler = (index: number): void => {
        const element = document?.getElementById(`footnote-${index}-text`);
        const closestAccordionEle = element?.closest("[data-test-id='Accordion.Content']");
        const closestTabEle = element?.closest("[role='tabpanel']");

        // check if footnote reference is present inside an accordion/tab
        const panes: Panes = { tabPaneId: null, accordionPaneId: null };
        if (closestAccordionEle) {
            const accordionPaneId = closestAccordionEle?.getAttribute("id");
            history.replaceState(null, "", `#${accordionPaneId}`);
            dispatch(setPanes({ ...panes, accordionPaneId }));
        } else if (closestTabEle) {
            const tabPaneId = closestTabEle?.getAttribute("data-target-tabname");
            history.replaceState(null, "", `#${tabPaneId}`);
            dispatch(setPanes({ ...panes, tabPaneId }));
        } else {
            history.replaceState(null, "", " ");
            dispatch(setPanes(panes));
        }
        setTimeout(() => {
            scrollIntoView(`footnote-${index}-text`, true);
            dispatch(setPanes(panes));
        }, 100);
    };

    return !hasFootnotes() ? null : (
        <div className={style.footnoteSection}>
            <div className={style.container} role="doc-endnotes">
                <div className={style.content}>
                    <div className={style.title}>
                        <Typography variant="h4" color="dark" component="h2" id="footnotes-label">
                            {t("References", { ns: "common" })}
                        </Typography>
                    </div>
                    {footnotes.map((f) =>
                        f.footnotes.map((footnote: Footnote, index: number) => {
                            if (!footnote.index) {
                                return;
                            }
                            return (
                                <div
                                    key={index}
                                    className={style.footnote}
                                    id={`footnote-${footnote.index}`}
                                    data-test-id={`footnote-${footnote.index}`}
                                >
                                    <Typography
                                        link
                                        onClick={(): void => onScrollHandler(footnote.index)}
                                        data-test-id={`footnote-${footnote.index}-index`}
                                    >
                                        [{footnote.index}]
                                    </Typography>
                                    <Typography color="light" size="s" data-test-id={`footnote-${footnote.index}-text`}>
                                        <JWMHtml placement={placementReferences} showGlossary={false}>
                                            {footnote.text}
                                        </JWMHtml>
                                    </Typography>
                                    <LinkButton
                                        href="#"
                                        className={style.backlink}
                                        onClick={(e): void => {
                                            e.preventDefault();
                                            onScrollHandler(footnote.index);
                                        }}
                                        role="doc-backlink"
                                        aria-label={t(`Back to reference {{FOOTNOTE_INDEX}}`, {
                                            FOOTNOTE_INDEX: footnote.index,
                                            ns: "common",
                                        })}
                                    >
                                        <Icon icon={mdiKeyboardReturn} size="xs" color="text-dark" />
                                    </LinkButton>
                                </div>
                            );
                        }),
                    )}
                </div>
            </div>
        </div>
    );
};

export default FootnotesSection;
